import { useMemo } from 'react';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { useBarbechoActivity } from '../../use-campaign-registry-activity';
import type { StageMessage } from './shared';

/**
 * "Barbecho" (fallow) banner — Escenario 1.
 *
 * A field is in Barbecho when it has no active crop monitoring (a field with an active crop
 * monitoring approaching its planting date is in Siembra, not Barbecho). When the previous
 * campaign closed without the user obtaining its harvest report, we prompt them to complete
 * it. The single action reuses the harvest-report flow; only its label changes: "Registrar
 * cosecha" once any campaign activity was registered (Escenario 1b/1c/1d — herbicide
 * application, herbicide recommendation, or hybrid recommendation), otherwise "Obtener reporte"
 * (Escenario 1a).
 *
 * Returns `null` when the field isn't in Barbecho, has no previous campaign, or that campaign
 * already has a harvest report — in every case the banner is hidden.
 */
export const useBarbechoStageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isBarbecho = !cropMonitoring?.active;

    const previousCropMonitoring = useMemo(
        () =>
            [...(field.cropMonitoring ?? [])]
                .filter(
                    (cm) =>
                        !cm.active &&
                        cm.campaign != null &&
                        (Number(cm.campaign) === field.campaign || Number(cm.campaign) === field.campaign - 1)
                )
                .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0],
        [field.cropMonitoring, field.campaign]
    );

    const showBanner = isBarbecho && Boolean(previousCropMonitoring) && !previousCropMonitoring?.result?.harvestReport;

    const { hasActivity, isLoading } = useBarbechoActivity({
        fieldId: showBanner ? field.id : undefined,
        cropMonitoring,
        enabled: showBanner,
    });

    if (!showBanner) return null;
    if (isLoading) return null;

    return {
        title: _('Obtené el reporte de la campaña anterior'),
        message: _(
            'Arrancó una nueva campaña y guardamos los datos de la anterior en Reportes de campaña. Registrá la cosecha para completar el reporte y analizarlo cuando lo necesites.'
        ),
        primaryAction: {
            label: hasActivity ? _('Registrar cosecha') : _('Obtener reporte'),
            action: 'harvest-report',
        },
    };
};
